import { Feather } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { useParams } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { FeedbackTray } from "@/components/Feedback/FeedbackTray";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";
import { FEEDBACK_FORM_URL } from "@/shared/constants";
import { CollapsibleCard } from "./CollapsibleCard";
import { isInsightPresent } from "@/utils/isInsightPresent";
import { UnavailableInsightCard } from "./UnavailableInsightCard";

interface SynopsisCardProps {
  synopsis?: string;
}

export function SynopsisCard({ synopsis }: SynopsisCardProps) {
  const { t } = useTranslation("jobDetails");
  const { jobId } = useParams({ from: "/jobs/$jobId", strict: true });
  const { features } = useFeatureFlags();

  if (!isInsightPresent.synopsis(synopsis)) {
    if (!features.synopsis) return null;
    return <UnavailableInsightCard insight="synopsis" />;
  }

  const paragraphs = synopsis
    .split(/\\n|\n/)
    .filter((p) => p.trim().length > 0);

  return (
    <FeedbackTray
      cardTestId="synopsis"
      jobId={jobId}
      contentType="synopsis"
      promptKey="feedback.synopsis.prompt"
    >
      <CollapsibleCard
        icon={Feather}
        title={t("synopsis.title")}
        testId="synopsis"
        showExperimentalBadge
      >
        {!features.inAppFeedback && (
          <p
            className="text-sm text-slate-400 mt-5 mb-4"
            data-testid="feedback-text"
          >
            <Trans
              t={t}
              i18nKey="synopsis.feedback"
              components={{
                1: (
                  <a
                    href={FEEDBACK_FORM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-500 underline font-medium"
                    data-testid="feedback-link"
                  />
                ),
              }}
            />
          </p>
        )}

        <div
          className={cn(
            "space-y-5 self-stretch",
            features.inAppFeedback && "mt-5",
          )}
        >
          {paragraphs.map((paragraph, index) => (
            <p
              key={`synopsis-paragraph-${index}`}
              className="job-details-text whitespace-pre-wrap self-stretch"
            >
              {paragraph}
            </p>
          ))}
        </div>
      </CollapsibleCard>
    </FeedbackTray>
  );
}
